import { useCallback, useContext, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Visibility,
  Edit,
} from "@mui/icons-material";
import dayjs from "dayjs";
import UserContext from "../Contexts/UserContext";
import {
  API_GetAllTimesheets,
  API_GetCandidateByID,
} from "../Assets/ApiUrlContainer";
import { default as axios } from "../axiosConfig";
import TimesheetDetails from "./TimesheetDetails";
import { Pagination } from "./Pagination";

const ManagerTimesheets = () => {
  const currentUser = useContext(UserContext);
  const [userIsTGSAdmin, setUserIsTGSAdmin] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [sortedTimesheets, setSortedTimesheets] = useState([]);
  const [filteredSortedTimesheets, setFilteredSortedTimesheets] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [allTimesheets, setAllTimesheets] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("WeekStartDate");
  const [sortOrder, setSortOrder] = useState("asc");
  const [jobs, setJobs] = useState([]);
  const [candidateNames, setCandidateNames] = useState({});
  const [start, setStart] = useState(0);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    notSubmitted: false,
    submitted: true,
    approvedByManager: true,
    approvedByTGS: true,
  });

  const userEmail = currentUser?.user?.Email;

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  const fetchCandidateNames = useCallback(
    async (candidateID) => {
      if (!candidateID || candidateNames[candidateID]) return;
      try {
        const response = await axios.get(`${API_GetCandidateByID}/${candidateID}`);
        const name = response.data.Name || "Unknown Candidate";
        // store in cache to prevent repeated fetching
        setCandidateNames((prev) => ({ ...prev, [candidateID]: name }));
      } catch (error) {
        console.error("Error fetching candidate name:", error);
      }
    },
    [candidateNames]
  );

  const fetchAllTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_GetAllTimesheets, {
        params: { Email: userEmail, Role: currentUser.user.Role },
      });

      const timesheetsResponse = response.data.timesheets || [];
      let filteredTimesheets = timesheetsResponse; // ✅ use freshly fetched data

      if (currentUser.user.Role === "hiring-manager") {
        filteredTimesheets = allTimesheets.filter(
          (ts) =>
            ts.SubmittedDate &&
            (!ts.ApprovingHiringManagerId || !ts.ApprovingTgsManagerId)
        );
      }

      if (currentUser.user.Role === "tgs-admin") {
        filteredTimesheets = allTimesheets.filter(
          (ts) =>
            ts.ApprovingHiringManagerId &&
            !ts.ApprovingTgsManagerId &&
            !ts.RejectedBy
        );
      }

      setTimesheets(filteredTimesheets);
      setJobs(response.data.jobs || []);
      setAllTimesheets(timesheetsResponse); // ✅ store full timesheets
    } catch (error) {
      console.error("Error fetching timesheets:", error);
    } finally {
      setLoading(false);
    }
  }, [userEmail, currentUser.user.Role]);

  useEffect(() => {
    if (userEmail) fetchAllTimesheets();
  }, [userEmail, fetchAllTimesheets]);

  useEffect(() => {
    const ids = [...new Set(allTimesheets.map((ts) => ts.CandidateId))];
    ids.forEach((id) => fetchCandidateNames(id));
  }, [allTimesheets, fetchCandidateNames]);

  useEffect(() => {
    setUserIsTGSAdmin(
      currentUser.user.IsAdmin && currentUser.user.IsGoogleUser
    );
  }, []);

  useEffect(() => {
    let filteredTimesheets = getFilterTimesheets();
    setTimesheets(filteredTimesheets)
  }, [filters, allTimesheets])

  useEffect(() => {
    if (timesheets.length === 0 || jobs.length === 0) return;

    let sortedTimesheets = [...timesheets].sort((a, b) => {
      // For submission date logic
      if (sortField === "SubmittedDate") {
        const aSubmitted = !!a.SubmittedDate;
        const bSubmitted = !!b.SubmittedDate;

        // Case 1: Both unsubmitted — sort by start date
        if (!aSubmitted && !bSubmitted) {
          return dayjs(convertDateForDayJS(a.WeekStartDate)).diff(dayjs(convertDateForDayJS(b.WeekStartDate)));
        }

        // Case 2: One submitted, one not — unsubmitted first if asc
        if (aSubmitted !== bSubmitted) {
          return sortOrder === "asc"
            ? aSubmitted ? 1 : -1
            : aSubmitted ? -1 : 1;
        }

        // Case 3: Both submitted — sort by submission date
        return sortOrder === "asc"
          ? dayjs(convertDateForDayJS(a.SubmittedDate)).diff(dayjs(convertDateForDayJS(b.SubmittedDate)))
          : dayjs(convertDateForDayJS(b.SubmittedDate)).diff(dayjs(convertDateForDayJS(a.SubmittedDate)));
      }

      // For JobId with secondary sort by start date
      if (sortField === "Job") {
        const aJob =
          jobs.find((j) => j.Id === a.JobId)?.Name?.toLowerCase() || "";
        const bJob =
          jobs.find((j) => j.Id === b.JobId)?.Name?.toLowerCase() || "";

        const primaryCompare =
          sortOrder === "asc"
            ? aJob.localeCompare(bJob)
            : bJob.localeCompare(aJob);

        if (primaryCompare !== 0) return primaryCompare;

        return dayjs(convertDateForDayJS(a.WeekStartDate)).diff(dayjs(convertDateForDayJS(b.WeekStartDate)));
      }

      // Generic date sorting fallback
      if (sortField.includes("Date")) {
        const aDate = dayjs(convertDateForDayJS(a[sortField]));
        const bDate = dayjs(convertDateForDayJS(b[sortField]));
        return sortOrder === "asc" ? aDate.diff(bDate) : bDate.diff(aDate);
      }

      // Fallback for non-date numeric fields
      return sortOrder === "asc"
        ? a[sortField] - b[sortField]
        : b[sortField] - a[sortField];
    });

    setSortedTimesheets(sortedTimesheets);
  }, [sortField, sortOrder, timesheets, jobs])

  useEffect(() => handleTimesheetFiltering(), [sortedTimesheets])

  const getApprovalStatus = (ts) => {
    let isUserAdmin = currentUser.user.Role === "tgs-admin";
    if (isUserAdmin) {
      return (ts.ApprovingTgsManagerId ? "Approved by TGS" : "Approved by Manager");
    } else {
      return (
        ts.ApprovingTgsManagerId ? "Approved by TGS" :
          ts.ApprovingHiringManagerId ? "Approved by Manager" :
            ts.SubmittedDate ? "Pending Approval" : "Pending Submission"
      )
    }
  }

  const getFilterTimesheets = () => allTimesheets.filter((ts) => {
    let status = getApprovalStatus(ts);

    const isSubmitted = !!ts.SubmittedDate && status === "Pending Approval";
    const isApprovedByManager = !!ts.ApprovingHiringManagerId && !ts.RejectedBy && status === "Approved by Manager";
    const isApprovedByTGS = !!ts.ApprovingTgsManagerId && !ts.RejectedBy && status === "Approved by TGS";

    if (!isSubmitted && filters.notSubmitted) return true;
    if (isSubmitted && !isApprovedByManager && !isApprovedByTGS && filters.submitted) return true;
    if (isApprovedByManager && filters.approvedByManager) return true;
    if (isApprovedByTGS && filters.approvedByTGS) return true;
    return false;
  });

  const handleTimesheetSelection = (ts) => setSelectedTimesheet(ts);

  const resetSortField = (updatedSortField) => {
    setSortField(updatedSortField);
    setSortOrder("asc");
  }

  const handleTimesheetFiltering = (filterString = searchText) => {
    setSearchText(filterString);
    if (filterString == "") setFilteredSortedTimesheets(sortedTimesheets);
    else {
      let matchingJobIds = jobs
        .filter(job => job.Name.toLowerCase().includes(filterString.toLowerCase()))
        .map(job => job.Id);

      let candidateNameEntries = Object.entries(candidateNames);
      let matchingCandidateIds = candidateNameEntries
        .filter(entry => entry[1].toLowerCase().includes(filterString.toLowerCase()))
        .map(entry => Number(entry[0]));

      let filteredTimesheets = sortedTimesheets.filter(ts => matchingCandidateIds.includes(ts.CandidateId) || matchingJobIds.includes(ts.JobId));
      setFilteredSortedTimesheets(filteredTimesheets);
    }
  }

  return (
    <div className="">
      <div className="tasksContainer">

        {!selectedTimesheet && (
          <>
            <div className="tasksHeader">
              {/* Sorting Dropdown */}
              <div className="sortingContainer">
                <label>Sort By:</label>
                <Select
                  value={sortField}
                  onChange={(e) => resetSortField(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <MenuItem value="WeekStartDate">Start Date</MenuItem>
                  <MenuItem value="SubmittedDate">Submission Date</MenuItem>
                  <MenuItem value="Job">Job</MenuItem>
                </Select>
                <IconButton
                  onClick={() =>
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                  }
                  size="small"
                  sx={{
                    ml: 1,
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    p: "4px",
                  }}
                >
                  {sortOrder === "desc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </IconButton>
              </div>
            </div>

            <FormGroup row sx={{ mb: 2 }}>
              {currentUser.user.Role !== "tgs-admin" && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.notSubmitted}
                        onChange={() =>
                          setFilters((prev) => ({
                            ...prev,
                            notSubmitted: !prev.notSubmitted,
                          }))
                        }
                      />
                    }
                    label="Not Submitted"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.submitted}
                        onChange={() =>
                          setFilters((prev) => ({
                            ...prev,
                            submitted: !prev.submitted,
                          }))
                        }
                      />
                    }
                    label="Submitted (Pending Approval)"
                  />
                </>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.approvedByManager}
                    onChange={() =>
                      setFilters((prev) => ({
                        ...prev,
                        approvedByManager: !prev.approvedByManager,
                      }))
                    }
                  />
                }
                label="Approved by Manager"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.approvedByTGS}
                    onChange={() =>
                      setFilters((prev) => ({
                        ...prev,
                        approvedByTGS: !prev.approvedByTGS,
                      }))
                    }
                  />
                }
                label="Approved by TGS"
              />

            </FormGroup>
          </>
        )}


        {selectedTimesheet ? (
          <TimesheetDetails
            role={
              currentUser.user.Role === "admin"
                ? "tgs-admin"
                : currentUser.user.Role
            }
            timesheet={selectedTimesheet}
            email={userEmail}
            managerId={currentUser?.user?.Id}
            onBack={() => {
              setSelectedTimesheet(null);
              fetchAllTimesheets();
            }}
          />
        ) : (
          <>
            <div className="header-tsManager">
              <h3>All Timesheets</h3>
              <div className="filterBar">
                <input id="timesheetFilter" placeholder="Search by job or contractor" onInput={(e) => handleTimesheetFiltering(e.target.value)} />
              </div>
            </div>
            {loading ? (
              <CircularProgress />
            ) : (
              <>
                <TableContainer component={Paper}>
                  <Table sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "10%" }}>
                          <strong>Start Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Job</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Hours</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Contractor/Employee</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell sx={{ width: "12%" }}>
                          <strong>Submission Date</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSortedTimesheets.slice(start, limit).map((ts) => (
                        <TableRow
                          key={`${ts.WeekStartDate}-${ts.JobId}`}
                          sx={{
                            backgroundColor:
                              ts.SubmittedDate &&
                                (!ts.ApprovingHiringManagerId ||
                                  !ts.ApprovingTgsManagerId) &&
                                dayjs().diff(dayjs(convertDateForDayJS(ts.SubmittedDate)), "day") > 30
                                ? "#EF9A9A" // light yellow for stale
                                : "inherit",
                          }}
                        >
                          <TableCell>
                            {dayjs(convertDateForDayJS(ts.WeekStartDate)).format("MMM D, YYYY")}
                          </TableCell>
                          <TableCell>
                            {jobs.find((job) => job.Id === ts.JobId)?.Name ||
                              "Unknown Job"}
                          </TableCell>
                          <TableCell>{ts.HoursWorked || "0"}</TableCell>
                          <TableCell>
                            {candidateNames[ts.CandidateId] || "Loading..."}
                          </TableCell>
                          <TableCell>
                            {getApprovalStatus(ts)}
                          </TableCell>

                          <TableCell>
                            {ts.SubmittedDate
                              ? dayjs(convertDateForDayJS(ts.SubmittedDate)).format("MMM D, YYYY")
                              : "N/A"}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              variant="outlined"
                              onClick={() => handleTimesheetSelection(ts)}
                              sx={{ color: "#673ab7", borderColor: "#673ab7" }}
                            >
                              {!userIsTGSAdmin &&
                                (ts.SubmittedDate &&
                                  !ts.ApprovingHiringManagerId ? (
                                  <Edit />
                                ) : (
                                  <Visibility />
                                ))}

                              {userIsTGSAdmin &&
                                (ts.ApprovingHiringManagerId &&
                                  !ts.ApprovingTgsManagerId ? (
                                  <Edit />
                                ) : (
                                  <Visibility />
                                ))}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination
                    totalNumItems={filteredSortedTimesheets.length}
                    onPageChangeNewPosition={setStart}
                    onRowsChangeNewDisplayLimit={setLimit}
                  />
                </TableContainer>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ManagerTimesheets;
